import React, { useEffect, useState } from 'react';
import { GLOBAL_SECTIONS, Pill } from '../data';
import { InstituteAutomationService, ApiGlobalConfig, ApiGlobalComponent, ComponentPatch } from '../instituteAutomationService';
import CompRow, { CompEdit } from './CompRow';

// Per-section chrome (emoji + dot + count badge) keyed by the seeded section
// codes, so live data keeps the mockup look. Unknown codes get a neutral style.
const SECTION_CHROME: Record<string, { emoji: string; dot: string; countClass: string; countStyle?: React.CSSProperties }> = {
    profile_data_coverage: { emoji: '📊', dot: '#1b52c4', countClass: 'badge bg-primary-subtle text-primary font-10 font-500 px-2 py-0.5 rounded-pill ms-1' },
    academic_professional_insights: { emoji: '🎓', dot: '#7c3aed', countClass: 'badge font-10 font-500 px-2 py-0.5 rounded-pill ms-1', countStyle: { backgroundColor: '#ede9fe', color: '#7c3aed' } },
    outreach_activities: { emoji: '📞', dot: '#64748b', countClass: 'badge bg-secondary bg-opacity-10 text-secondary font-10 font-500 px-2 py-0.5 rounded-pill ms-1' },
    campaign_event_activities: { emoji: '📣', dot: '#0891b2', countClass: 'badge bg-info-subtle text-info font-10 font-500 px-2 py-0.5 rounded-pill ms-1' },
    platform_activity: { emoji: '💻', dot: '#0f9d58', countClass: 'badge bg-success-subtle text-success font-10 font-500 px-2 py-0.5 rounded-pill ms-1' },
};
const DEFAULT_CHROME = { emoji: '📄', dot: '#64748b', countClass: 'badge bg-secondary bg-opacity-10 text-secondary font-10 font-500 px-2 py-0.5 rounded-pill ms-1' };

// Frequency label ↔ months (spec D2: 1/3/6/12 only).
const FREQ_LABELS = ['Monthly', 'Quarterly', 'Semi-Annual', 'Annual'];
const MONTHS_TO_LABEL: Record<number, string> = { 1: 'Monthly', 3: 'Quarterly', 6: 'Semi-Annual', 12: 'Annual' };
const LABEL_TO_MONTHS: Record<string, number> = { Monthly: 1, Quarterly: 3, 'Semi-Annual': 6, Annual: 12 };
const freqToLabel = (m: number) => MONTHS_TO_LABEL[m] ?? 'Monthly';
const labelToMonths = (l: string) => LABEL_TO_MONTHS[l] ?? 1;

// Scope pill (Both / K-12 Only / College) from audience.
const scopePill = (a: string | null): Pill => {
    if (a === 'k12') return { label: 'K-12 Only', className: 'badge font-10 font-500 px-2 py-1', style: { backgroundColor: '#ede9fe', color: '#7c3aed' } };
    if (a === 'degree' || a === 'college') return { label: 'College', className: 'badge bg-info-subtle text-info font-10 font-500 px-2 py-1 rounded-pill' };
    return { label: 'Both', className: 'badge bg-primary-subtle text-primary font-10 font-500 px-2 py-1 rounded-pill' };
};
// Priority pill (High / Medium / Low).
const priorityPill = (p: string | null): Pill => {
    if (p === 'medium') return { label: 'Medium', className: 'badge bg-warning-subtle text-warning-emphasis font-10 font-500 px-2 py-1' };
    if (p === 'low') return { label: 'Low', className: 'badge bg-light text-secondary font-10 font-500 px-2 py-1' };
    return { label: 'High', className: 'badge bg-success-subtle text-success font-10 font-500 px-2 py-1 rounded-pill' };
};

// Trim trailing decimal zeros: "2.0000" → "2", "1.5000" → "1.5".
const trimNum = (v: string | number): string => {
    const s = String(v);
    return /^-?\d+\.\d+$/.test(s) ? String(Number(s)) : s;
};

// Seed an editable state object from an API component.
const toEdit = (c: ApiGlobalComponent): CompEdit => ({
    frequencyMonths: c.frequencyMonths ?? 1,
    minValue: c.minValue == null ? '' : trimNum(c.minValue),
    isActive: c.isActive,
});

const GlobalSettings: React.FC<{ toast: (msg: string) => void }> = ({ toast }) => {
    const [config, setConfig] = useState<ApiGlobalConfig | null>(null);
    const [source, setSource] = useState<'loading' | 'live' | 'fallback'>('loading');
    const [open, setOpen] = useState<Record<string, boolean>>({});
    // Editable state keyed by component id; `baseline` is the last-saved snapshot.
    const [edit, setEdit] = useState<Record<number, CompEdit>>({});
    const [baseline, setBaseline] = useState<Record<number, CompEdit>>({});
    const [saving, setSaving] = useState(false);
    // Single report-level send frequency (applies to ALL institutes).
    const [reportFreq, setReportFreq] = useState<number | null>(null);
    const [applyingFreq, setApplyingFreq] = useState(false);
    // Per-section send frequency (0 = Skip). Applies to all clients.
    const [sectionFreq, setSectionFreq] = useState<Record<number, number>>({});

    const applySectionFreq = async (sectionId: number, freq: number) => {
        setSectionFreq((prev) => ({ ...prev, [sectionId]: freq }));
        try {
            await InstituteAutomationService.updateSectionFrequency(sectionId, freq);
            toast(freq === 0 ? 'Section set to Skip — it won’t be sent' : `Section frequency set to ${MONTHS_TO_LABEL[freq]}`);
        } catch (_) {
            toast('Could not update section frequency (run the report_section migration?)');
        }
    };

    useEffect(() => {
        InstituteAutomationService.getGlobalFrequency().then((r) => setReportFreq(r.frequencyMonths)).catch(() => setReportFreq(null));
    }, []);

    const applyReportFrequency = async (freq: number) => {
        setReportFreq(freq); setApplyingFreq(true);
        try {
            const r = await InstituteAutomationService.setGlobalFrequency(freq);
            const d = new Date(r.nextSendDate);
            toast(`All clients set to ${MONTHS_TO_LABEL[freq]} — ${r.appliedToSchedules} schedule${r.appliedToSchedules === 1 ? '' : 's'} updated (next send ${isNaN(d.getTime()) ? r.nextSendDate : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })})`);
        } catch (_) {
            toast('Could not apply report frequency');
        } finally {
            setApplyingFreq(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const cfg = await InstituteAutomationService.getComponents();
                if (cancelled) return;
                if (!cfg?.sections?.length) throw new Error('No sections');
                const seed: Record<number, CompEdit> = {};
                const sf: Record<number, number> = {};
                cfg.sections.forEach((s) => { sf[s.id] = s.frequencyMonths; s.components.forEach((c) => { seed[c.id] = toEdit(c); }); });
                setConfig(cfg);
                setEdit(seed);
                setBaseline(seed);
                setSectionFreq(sf);
                setOpen({ [String(cfg.sections[0].id)]: true });
                setSource('live');
            } catch (_) {
                if (!cancelled) { setSource('fallback'); setOpen({ [GLOBAL_SECTIONS[0].id]: true }); }
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const toggle = (id: string) => setOpen((prev) => ({ ...prev, [id]: !prev[id] }));
    const patchRow = (id: number, patch: Partial<CompEdit>) => setEdit((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

    // Which components differ from their last-saved baseline.
    const changedIds = Object.keys(edit).map(Number).filter((id) => {
        const a = edit[id], b = baseline[id];
        return !b || a.frequencyMonths !== b.frequencyMonths || a.minValue !== b.minValue || a.isActive !== b.isActive;
    });
    const dirty = changedIds.length;

    const save = async () => {
        if (source !== 'live' || !dirty || saving) return;
        setSaving(true);
        try {
            await Promise.all(changedIds.map((id) => {
                const a = edit[id], b = baseline[id];
                const patch: ComponentPatch = {};
                if (!b || a.frequencyMonths !== b.frequencyMonths) patch.default_frequency_months = a.frequencyMonths;
                if (!b || a.minValue !== b.minValue) patch.default_min_value = a.minValue.trim() === '' ? null : Number(a.minValue);
                if (!b || a.isActive !== b.isActive) patch.is_active = a.isActive;
                return InstituteAutomationService.updateComponent(id, patch);
            }));
            setBaseline(edit); // new saved snapshot
            toast(`Global settings saved ✔ (${dirty} component${dirty === 1 ? '' : 's'})`);
        } catch (_) {
            toast('Could not save global settings — please try again');
        } finally {
            setSaving(false);
        }
    };

    // ── Live section card (editable rows). Sections with no components are hidden. ──
    const renderLive = (cfg: ApiGlobalConfig) => cfg.sections.filter((s) => s.components.length > 0).map((s) => {
        const chrome = SECTION_CHROME[s.code] ?? DEFAULT_CHROME;
        const id = String(s.id);
        return (
            <div className="card border border-light shadow-sm rounded-3 mb-3 bg-white" key={id}>
                <div className="card-header bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center" style={{ cursor: 'pointer' }} onClick={() => toggle(id)}>
                    <div className="font-14 font-600 text-dark d-flex align-items-center gap-2">
                        <span className="rounded-circle d-inline-block" style={{ width: 10, height: 10, backgroundColor: chrome.dot }} />
                        {chrome.emoji} {s.name}
                        <span className={chrome.countClass} style={chrome.countStyle}>{s.components.length} component{s.components.length === 1 ? '' : 's'}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <span className="font-10 text-secondary text-uppercase">Send</span>
                        <select className="form-select form-select-sm font-12 py-1 px-2 border" style={{ width: 'auto' }}
                            value={sectionFreq[s.id] ?? s.frequencyMonths}
                            onChange={(e) => applySectionFreq(s.id, Number(e.target.value))}>
                            <option value={0}>Skip</option>
                            <option value={1}>Monthly</option>
                            <option value={3}>Quarterly</option>
                            <option value={6}>Semi-Annual</option>
                            <option value={12}>Annual</option>
                        </select>
                        <span className="text-secondary font-11" style={{ cursor: 'pointer' }} onClick={() => toggle(id)}>▼</span>
                    </div>
                </div>
                <div className={`collapse ${open[id] ? 'show' : ''} border-top border-light`}>
                    <div className="card-body p-0">
                        {s.components.map((c, i) => (
                            <CompRow
                                key={c.id} last={i === s.components.length - 1} hideFreq
                                row={{ name: c.name, desc: c.description || '', freq: '', threshold: '', pills: [scopePill(c.audience), priorityPill(c.priority)] }}
                                edit={edit[c.id]} freqOptions={FREQ_LABELS}
                                freqToLabel={freqToLabel} labelToMonths={labelToMonths}
                                onChange={(patch) => patchRow(c.id, patch)}
                            />
                        ))}
                    </div>
                </div>
            </div>
        );
    });

    // ── Fallback: static mockup (read-only) ──
    const renderFallback = () => GLOBAL_SECTIONS.map((section) => (
        <div className="card border border-light shadow-sm rounded-3 mb-3 bg-white" key={section.id}>
            <div className="card-header bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center" style={{ cursor: 'pointer' }} onClick={() => toggle(section.id)}>
                <div className="font-14 font-600 text-dark d-flex align-items-center gap-2">
                    <span className="rounded-circle d-inline-block" style={{ width: 10, height: 10, backgroundColor: section.dot }} />
                    {section.title}
                    <span className={section.count.className} style={section.count.style}>{section.count.text}</span>
                </div>
                <span className="text-secondary font-11">▼</span>
            </div>
            <div className={`collapse ${open[section.id] ? 'show' : ''} border-top border-light`}>
                <div className="card-body p-0">
                    {section.rows.map((row, i) => <CompRow key={i} row={row} last={i === section.rows.length - 1} />)}
                </div>
            </div>
        </div>
    ));

    return (
        <>
            <div className="alert alert-warning border border-warning-subtle rounded-3 p-3 mb-4 d-flex flex-wrap align-items-center gap-3 bg-warning bg-opacity-10">
                <span className="font-20">⚠️</span>
                <div className="flex-grow-1">
                    <div className="font-13 font-600 text-warning-emphasis">These are Global Defaults — applied to ALL clients</div>
                    <div className="font-11 text-secondary mt-1">Changes here affect every client's report unless a client-specific override is set. Use the Client Settings tab to override per client.</div>
                </div>
                {dirty > 0 && <span className="font-11 text-warning-emphasis font-600">{dirty} unsaved change{dirty === 1 ? '' : 's'}</span>}
                <button
                    className="btn btn-sm text-white font-12 font-600 px-3 py-2 border-0"
                    style={{ backgroundColor: '#f97316', opacity: source === 'live' && dirty && !saving ? 1 : 0.6 }}
                    disabled={source !== 'live' || !dirty || saving}
                    onClick={save}
                >
                    {saving ? 'Saving…' : 'Save Changes'}
                </button>
            </div>

            {/* Report send frequency — one value, applied to every institute's schedule */}
            <div className="card border border-primary-subtle shadow-sm rounded-3 mb-4 bg-white">
                <div className="card-body d-flex flex-wrap align-items-center gap-3 py-3 px-4">
                    <div className="flex-grow-1">
                        <div className="font-13 font-600 text-dark">📅 Report Send Frequency</div>
                        <div className="font-11 text-secondary mt-1">How often every client's report is emailed. Changing this updates <strong>all</strong> institutes' schedules to the 1st of the period.</div>
                    </div>
                    <select className="form-select form-select-sm font-13" style={{ width: 'auto' }} value={reportFreq ?? 1} disabled={reportFreq == null || applyingFreq}
                        onChange={(e) => applyReportFrequency(Number(e.target.value))}>
                        <option value={1}>Monthly — 1st of every month</option>
                        <option value={3}>Quarterly — 1st of Jan/Apr/Jul/Oct</option>
                        <option value={6}>Semi-Annual — 1st of Jan/Jul</option>
                        <option value={12}>Annual — 1st of January</option>
                    </select>
                    {applyingFreq && <span className="spinner-border spinner-border-sm text-primary" />}
                </div>
            </div>

            {source === 'fallback' && (
                <div className="font-11 text-secondary mb-3"><i className="bi bi-info-circle me-1" />Showing sample configuration — live report data unavailable, editing disabled.</div>
            )}

            {source === 'live' && config ? renderLive(config) : renderFallback()}
        </>
    );
};

export default GlobalSettings;
